import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';  
import { Navigate, Outlet, useLocation, useOutlet, useSearchParams } from 'react-router-dom';  
  
import { LocaleContext, LoginContext, OverlayContext, ScrollContext, StateContext } from './common/App';
import { ConsumerContext } from '.';
import { DeactivateAccountPopup, ForwardsBackwardsBar, LoadButton, PopupFixed, RadioCheckButton, SquareButton } from './common/Common';
import "./CustomerRoute.css";
import { logout, logoutConsumer, postWithCsrf, resetLoginStatesConsumer } from "./common/util";
import { InactivityAnswer, LoadState, UserType } from './common/enums.mjs';
import { useNavigateSmart } from './common/hooks';

export const CustomerRoute = () => {
    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);
    const state = useContext(StateContext);
    const consumer = useContext(ConsumerContext);
    const overlay = useContext(OverlayContext);
    const scroll = useContext(ScrollContext);

    const [logoutLoadState, setLoadState] = useState(LoadState.NONE);
    const [inactivityQueryLoadState, setInactivityQueryLoadState] = useState(LoadState.NONE);

    const [inactivityAnswer, setInactivityAnswer] = useState(InactivityAnswer.NONE);

    const navigate = useNavigateSmart();
    const location = useLocation();

    const loggedInRef = useRef(false);


    // FUNCTIONS:

    function handleLogoutClick() {
        try {
            logoutConsumer(state, login, consumer).then((response) => {
                setLoadState(LoadState.LOAD_SUCCESS);
                navigate("/");
                scroll.scrollToPlain(0, 0);
            }).catch((error) => {
                console.log(error);
                setLoadState(LoadState.LOAD_ERROR);
            });

            // Calling setLoadState() after logoutConsumer() as it could throw an exception
            setLoadState(LoadState.LOADING);
		}
		catch(e) {
			console.log(e);
		}
    }

    function handleInactivityQueryClick() {
        try {
            postWithCsrf("/user/set_inactivity_answer", JSON.stringify({"inactivity_answer": inactivityAnswer}), state).then(response => {
                if(response.ok) {
                    return response.json();
                }
                else {
                    throw new Error("Response code: " + response.status);
                }
            }).then(json => {
                setInactivityQueryLoadState(LoadState.LOAD_SUCCESS);

                if(inactivityAnswer === InactivityAnswer.DELETE_ACCOUNT_NOW) {
                    resetLoginStatesConsumer(login, consumer);
                }
                else {
                    consumer.setInactivityQuery(json.inactivity_status);
                }
            }).catch(error => {
                setInactivityQueryLoadState(LoadState.LOAD_ERROR);
                console.log(error);
            });

            // Calling setInactivityQueryLoadState() after postWithCsrf() as it could throw an exception
            setInactivityQueryLoadState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }

    function shouldShowInactivityQuery() {
        return consumer.inactivityQuery && login.userType === UserType.NANNY && login.twoFactorAuthEnabled;
    }


    // EFFECTS:

    useLayoutEffect(() => {
        console.log(consumer.inactivityQuery)
        if(consumer.declined || shouldShowInactivityQuery()) {
            overlay.setOverlaySolid(true);
        }

        return () => {
            overlay.setOverlaySolid(false);
        };
    }, [consumer.declined, consumer.inactivityQuery, login.userType, login.twoFactorAuthEnabled]);

    useLayoutEffect(() => {
        loggedInRef.current = login.loggedIn;
    }, [login.loggedIn]);

    useLayoutEffect(() => {
        if(!loggedInRef.current) {
            return;
        }

        const searchParams = new URLSearchParams(location.search);

        // Get the value of the query parameter you're interested in  
        const tab = searchParams.get("unsubscribe");


        if(tab !== null) {
            consumer.setDeleteAccountDisplayed(true);
            navigate(location.pathname);
        }
    }, [location.pathname, location.search]);

    useLayoutEffect(() => {
        if(consumer.deleteAccountDisplayed) {
            // This needs to be done in the next render cycle as the overlay will be set to false by other popups
            overlay.setOverlay(true);
        }
    }, [consumer.deleteAccountDisplayed]);


    // RENDER:

    let display = "";

    if(consumer.deleteAccountDisplayed) {
        display = "delete_account";
    }
    else if(consumer.declined) {
        display = "declined";
    }
    else if(shouldShowInactivityQuery()) {
        display = "inactivity_query";
    }

    return (
        <div className="customer_route">
            <PopupFixed helpDisplayed={display === "inactivity_query"}
                    content={
                        <div className="inactivity_query_box side_padding">
                            <div className="head">
                                {locale.msgs.INACTIVITY_QUERY}
                            </div>

                            <div className="inactivity_query_choices_box">
                                <RadioCheckButton type="radio" text={locale.msgs.INACTIVITY_QUERY_CHOICE_1} id="inactivity_query_choice_1"
                                        name="inactivity_query_choice_1"
                                        checked={inactivityAnswer === InactivityAnswer.DELETE_ACCOUNT_IN_12_MONTHS}
                                        onChange={(e) => setInactivityAnswer(InactivityAnswer.DELETE_ACCOUNT_IN_12_MONTHS)}
                                        multiline={true}
                                />

                                 <RadioCheckButton type="radio" text={locale.msgs.INACTIVITY_QUERY_CHOICE_2} id="inactivity_query_choice_2"
                                        name="inactivity_query_choice_2"
                                        checked={inactivityAnswer === InactivityAnswer.DELETE_ACCOUNT_IN_4_MONTHS}
                                        onChange={(e) => setInactivityAnswer(InactivityAnswer.DELETE_ACCOUNT_IN_4_MONTHS)}
                                        multiline={true}
                                />

                                 <RadioCheckButton type="radio" text={locale.msgs.INACTIVITY_QUERY_CHOICE_3} id="inactivity_query_choice_3"
                                        name="inactivity_query_choice_3"
                                        checked={inactivityAnswer === InactivityAnswer.DELETE_ACCOUNT_NOW}
                                        onChange={(e) => setInactivityAnswer(InactivityAnswer.DELETE_ACCOUNT_NOW)}
                                        multiline={true}
                                />
                            </div>

                            <div className="inactivity_query_logout_btn_box">
                                <LoadButton clickable={true} text={locale.msgs.LOGOUT} onClick={handleLogoutClick}
                                        saving={logoutLoadState === LoadState.LOADING} errText={locale.msgs.ERROR}
                                        notSaved={logoutLoadState === LoadState.LOAD_ERROR}
                                        white={true} />

                                <LoadButton clickable={inactivityAnswer !== InactivityAnswer.NONE} text={locale.msgs.CONTINUE} onClick={handleInactivityQueryClick}
                                        saving={inactivityQueryLoadState === LoadState.LOADING} errText={locale.msgs.ERROR}
                                        notSaved={inactivityQueryLoadState === LoadState.LOAD_ERROR} />
                            </div>
                        </div>
                    }
            />

            <DeactivateAccountPopup confirmDisplayed={display === "delete_account"} setConfirmDisplayed={consumer.setDeleteAccountDisplayed}
                    userActive={true}
                    onAccountDeactivated={() => {
                        resetLoginStatesConsumer(login, consumer);
                    }}

                    /* onAccountReactivated={() => {
                        setUser({...user, active: true});
                    }}
                    */
            />

            <PopupFixed helpDisplayed={display === "declined"}
                    content={
                        <div className="declined_box side_padding">
                            <div className="head">
                                {locale.msgs.WE_ARE_SORRY}
                            </div>
                            
                            <div className="text_2" dangerouslySetInnerHTML={{__html: locale.msgs.WE_ARE_SORRY_DESCR}}></div>

                            <div className="declined_logout_btn_box">
                                <LoadButton clickable={true} small={true} white={false} text={locale.msgs.LOGOUT}
                                        onClick={handleLogoutClick} saving={logoutLoadState === LoadState.LOADING}
                                        notSaved={logoutLoadState === LoadState.LOAD_ERROR}
                                        errText={locale.msgs.ERROR} />
                            </div>
                        </div>
                    }
            />

            { display !== "declined" && display !== "inactivity_query" ?
                /* We only want to render after the popup is gone because the requests for the dashboard data would be failing in the background
                   and after the popup disappears an error message would be visible for a certain time until a request was successful */
                <Outlet />
            :
                null
            }

        </div>
    );
};

export default CustomerRoute
